/** Профиль: свой доступ, показатели за месяц и история операций. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Avatar, Loading, Row, Stat, toast } from '../components/ui';
import { dateTime, money, moneySigned } from '../lib/format';
import type { LedgerEntry } from '../lib/types';

type Profile = {
  balance: number;
  monthStats: { earned: number; penalties: number; paidOut: number };
  videoCounts: Record<string, number>;
  history: LedgerEntry[];
};

export function ProfilePage() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => api.get<Profile>(`/api/users/${user!.id}`),
    enabled: Boolean(user),
  });

  if (!user) return null;

  const done = (data?.videoCounts.DONE ?? 0) + (data?.videoCounts.PUBLISHED ?? 0);

  return (
    <>
      <PageHeader title="Профиль" sub={user.displayName} />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="card p-6">
              <div className="flex items-center gap-4">
                <Avatar id={user.id} name={user.displayName} url={user.avatarUrl} size={60} />
                <div>
                  <div className="text-[21px] font-extrabold tracking-[-0.02em]">
                    {user.displayName}
                  </div>
                  <div className="text-muted2 mt-1">
                    {user.tgUsername ? `@${user.tgUsername}` : `Telegram ID ${user.tgId}`}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-6">
                <Stat value={money(data?.balance ?? 0)} label="баланс" />
                <Stat value={done} label="видео сдано" />
                <Stat
                  value={money(data?.monthStats.penalties ?? 0)}
                  label="штрафы за месяц"
                  tone={data?.monthStats.penalties ? 'dang' : undefined}
                />
              </div>
            </div>

            <div className="sec">История операций</div>
            {isLoading ? (
              <Loading rows={4} />
            ) : (
              <div className="card p-0 overflow-hidden">
                {data?.history.map((op) => (
                  <div
                    key={op.id}
                    className="flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24] last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{op.reason ?? op.type}</div>
                      <div className="text-[11.5px] text-muted2">{dateTime(op.createdAt)}</div>
                    </div>
                    <span className={`mono font-bold ${op.amount > 0 ? 'text-ok' : 'text-dang'}`}>
                      {moneySigned(op.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <AccessSettings />

            <div className="sec">За месяц</div>
            <div className="card">
              <Row label="Заработано">
                <span className="mono text-ok">{money(data?.monthStats.earned ?? 0)}</span>
              </Row>
              <Row label="Штрафы">
                <span className="mono text-dang">{money(data?.monthStats.penalties ?? 0)}</span>
              </Row>
              <Row label="Выплачено">
                <span className="mono">{money(data?.monthStats.paidOut ?? 0)}</span>
              </Row>
            </div>

            <div className="sec">Видео по статусам</div>
            <div className="card">
              {Object.entries(data?.videoCounts ?? {}).map(([status, count]) => (
                <Row key={status} label={statusLabel(status)}>
                  {count}
                </Row>
              ))}
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

/**
 * Свой доступ: логин и пароль.
 *
 * Человек должен уметь сменить их сам, не дёргая администратора.
 * Логин показывается сразу, чтобы не гадать, под каким именем входить.
 */
function AccessSettings() {
  const { user, reload } = useAuth();
  const [mode, setMode] = useState<null | 'password' | 'username' | 'telegram'>(null);
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [username, setUsername] = useState(user?.username ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const savePassword = async () => {
    if (next.length < 8) return toast('Пароль должен быть не короче 8 символов', '⚠');
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      toast('Пароль изменён');
      setMode(null);
      setCurrent('');
      setNext('');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось', '⚠');
    } finally {
      setBusy(false);
    }
  };

  const saveUsername = async () => {
    setBusy(true);
    try {
      await api.post('/api/auth/change-username', { username });
      toast('Логин изменён');
      setMode(null);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось', '⚠');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="sec !mt-0">Доступ</div>
      <div className="card">
        <Row label="Логин">
          {user?.username ? (
            <span className="mono">{user.username}</span>
          ) : (
            <span className="text-muted2 font-normal text-[12.5px]">не задан</span>
          )}
        </Row>
        <Row label="Telegram">
          {user?.tgId ? (
            <span className="text-ok">
              {user.tgUsername ? `@${user.tgUsername}` : 'привязан'}
            </span>
          ) : (
            <span className="text-muted2 font-normal text-[12.5px]">не привязан</span>
          )}
        </Row>

        <div className="pt-3 space-y-2">
          {mode === null && (
            <>
              <button className="btn btn-sm w-full" onClick={() => setMode('password')}>
                Сменить пароль
              </button>
              <button className="btn btn-sm w-full" onClick={() => setMode('username')}>
                {user?.username ? 'Сменить логин' : 'Задать логин'}
              </button>
              {!user?.tgId && (
                <button
                  className="btn btn-sm btn-primary w-full"
                  onClick={() => {
                    setMode('telegram');
                    void api
                      .post<{ code: string }>('/api/auth/link-telegram')
                      .then((r) => setLinkCode(r.code))
                      .catch((e) => toast(e instanceof Error ? e.message : 'Не удалось', '⚠'));
                  }}
                >
                  Привязать Telegram
                </button>
              )}
            </>
          )}

          {/* Код привязки. Живёт 15 минут — этого хватает, чтобы переключиться
              в Telegram и отправить, но забытый код не работает вечно */}
          {mode === 'telegram' && (
            <div className="space-y-2">
              <div className="text-[12.5px] text-muted2 leading-relaxed">
                Отправь этот код боту{' '}
                <span className="text-accent2">@VovchikProd_bot</span> — просто сообщением,
                команда не нужна.
              </div>
              <div className="bg-bg rounded-lg py-3 text-center">
                <div className="mono text-2xl font-extrabold tracking-[0.25em] text-accent2">
                  {linkCode ?? '······'}
                </div>
                <div className="text-[11px] text-muted2 mt-1">действует 15 минут</div>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm flex-1"
                  onClick={() => {
                    if (linkCode) {
                      void navigator.clipboard.writeText(linkCode);
                      toast('Код скопирован', '⧉');
                    }
                  }}
                >
                  Скопировать
                </button>
                <button
                  className="btn btn-sm flex-1"
                  onClick={() => {
                    setMode(null);
                    setLinkCode(null);
                    void reload();
                  }}
                >
                  Готово
                </button>
              </div>
            </div>
          )}

          {mode === 'password' && (
            <div className="space-y-2">
              <input
                className="input"
                type="password"
                placeholder="текущий пароль"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
              <input
                className="input"
                type="password"
                placeholder="новый пароль, минимум 8 символов"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn btn-sm flex-1" onClick={() => setMode(null)}>
                  Отмена
                </button>
                <button
                  className="btn btn-sm btn-primary flex-1"
                  onClick={savePassword}
                  disabled={busy}
                >
                  Сохранить
                </button>
              </div>
            </div>
          )}

          {mode === 'username' && (
            <div className="space-y-2">
              <input
                className="input mono"
                placeholder="латиница, цифры, от 3 символов"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn btn-sm flex-1" onClick={() => setMode(null)}>
                  Отмена
                </button>
                <button
                  className="btn btn-sm btn-primary flex-1"
                  onClick={saveUsername}
                  disabled={busy}
                >
                  Сохранить
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-[11.5px] text-muted2 mt-2.5 px-1 leading-relaxed">
        Логин и пароль нужны для входа через сайт — на случай, если Telegram
        окажется недоступен. Оба способа ведут в один и тот же аккаунт.
        {!user?.tgId && ' Без привязки Telegram уведомления приходить не будут.'}
      </div>
    </>
  );
}

const statusLabel = (s: string) =>
  ({
    NEW: 'Новые',
    SHOOTING: 'Съёмка',
    AWAITING_EDIT: 'Ждут монтаж',
    EDITING: 'Монтаж',
    REVIEW: 'На проверке',
    REVISION: 'Доработка',
    DONE: 'Готово',
    CANCELLED: 'Отменено',
    PUBLISHED: 'Опубликовано',
  })[s] ?? s;
